import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Upload, GripVertical, Image, Video, Pencil, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface HighlightItem {
  id: string;
  highlight_id: string;
  type: "image" | "video";
  media_url: string;
  caption: string | null;
  display_order: number;
}

interface Highlight {
  id: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  display_order: number;
  is_active: boolean;
  items: HighlightItem[];
}

export function HighlightsManager() {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedHighlight, setSelectedHighlight] = useState<Highlight | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchHighlights();
  }, []);

  const fetchHighlights = async () => {
    try {
      const { data: highlightsData, error: highlightsError } = await supabase
        .from("highlights")
        .select("*")
        .order("display_order");

      if (highlightsError) throw highlightsError;

      const { data: itemsData, error: itemsError } = await supabase
        .from("highlight_items")
        .select("*")
        .order("display_order");

      if (itemsError) throw itemsError;

      const highlightsWithItems: Highlight[] = (highlightsData || []).map((h) => ({
        id: h.id,
        title: h.title,
        description: h.description,
        cover_image_url: h.cover_image_url,
        display_order: h.display_order,
        is_active: h.is_active,
        items: (itemsData || [])
          .filter((item) => item.highlight_id === h.id)
          .map((item) => ({
            id: item.id,
            highlight_id: item.highlight_id,
            type: item.type as "image" | "video",
            media_url: item.media_url,
            caption: item.caption,
            display_order: item.display_order,
          })),
      }));

      setHighlights(highlightsWithItems);
    } catch (error) {
      console.error("Error fetching highlights:", error);
      toast({
        title: "Error",
        description: "Failed to load highlights",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateHighlight = async (id: string, updates: Partial<Highlight>) => {
    try {
      const { error } = await supabase
        .from("highlights")
        .update(updates)
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Highlight updated" });
      fetchHighlights();
    } catch (error) {
      console.error("Error updating highlight:", error);
      toast({
        title: "Error",
        description: "Failed to update highlight",
        variant: "destructive",
      });
    }
  };

  const uploadMedia = async (file: File, highlightId: string) => {
    setUploading(true);
    try {
      // Validate file size (max 150MB for videos, 10MB for images)
      const maxSize = file.type.startsWith("video/") ? 150 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024);
        throw new Error(`File too large. Maximum size is ${maxMB}MB`);
      }

      // For videos, validate duration (max 120 seconds)
      if (file.type.startsWith("video/")) {
        const duration = await getVideoDuration(file);
        if (duration > 120) {
          throw new Error(`Video too long. Maximum duration is 120 seconds (yours is ${Math.round(duration)}s)`);
        }
      }

      const fileExt = file.name.split(".").pop();
      const fileName = `${highlightId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("highlight-media")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("highlight-media")
        .getPublicUrl(fileName);

      const isVideo = file.type.startsWith("video/");
      
      // Get max display order
      const highlight = highlights.find(h => h.id === highlightId);
      const maxOrder = highlight?.items.reduce((max, item) => Math.max(max, item.display_order), 0) || 0;

      const { error: itemError } = await supabase
        .from("highlight_items")
        .insert({
          highlight_id: highlightId,
          type: isVideo ? "video" : "image",
          media_url: publicUrl,
          display_order: maxOrder + 1,
        });

      if (itemError) throw itemError;

      // If this is the first item and no cover, set as cover
      if (highlight && !highlight.cover_image_url && !isVideo) {
        await updateHighlight(highlightId, { cover_image_url: publicUrl });
      }

      toast({ title: "Success", description: "Media uploaded" });
      fetchHighlights();
    } catch (error: any) {
      console.error("Error uploading media:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload media",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // Helper to get video duration
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => reject(new Error('Failed to load video'));
      video.src = URL.createObjectURL(file);
    });
  };

  const deleteItem = async (item: HighlightItem) => {
    try {
      // Extract file path from URL
      const url = new URL(item.media_url);
      const pathParts = url.pathname.split("/highlight-media/");
      if (pathParts.length > 1) {
        await supabase.storage
          .from("highlight-media")
          .remove([pathParts[1]]);
      }

      const { error } = await supabase
        .from("highlight_items")
        .delete()
        .eq("id", item.id);

      if (error) throw error;

      toast({ title: "Success", description: "Media deleted" });
      fetchHighlights();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "Error",
        description: "Failed to delete media",
        variant: "destructive",
      });
    }
  };

  const updateItemCaption = async (itemId: string, caption: string) => {
    try {
      const { error } = await supabase
        .from("highlight_items")
        .update({ caption })
        .eq("id", itemId);

      if (error) throw error;
      fetchHighlights();
    } catch (error) {
      console.error("Error updating caption:", error);
    }
  };

  const setCoverImage = async (highlightId: string, imageUrl: string) => {
    await updateHighlight(highlightId, { cover_image_url: imageUrl });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Manage Highlights</h2>
      </div>

      <div className="grid gap-4">
        {highlights.map((highlight) => (
          <Card key={highlight.id} className={cn(!highlight.is_active && "opacity-60")}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                    {highlight.cover_image_url ? (
                      <img
                        src={highlight.cover_image_url}
                        alt={highlight.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Image className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{highlight.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">{highlight.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`active-${highlight.id}`} className="text-sm">Active</Label>
                    <Switch
                      id={`active-${highlight.id}`}
                      checked={highlight.is_active}
                      onCheckedChange={(checked) => updateHighlight(highlight.id, { is_active: checked })}
                    />
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedHighlight(highlight)}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Edit Highlight</DialogTitle>
                      </DialogHeader>
                      <EditHighlightForm 
                        highlight={highlight} 
                        onSave={(updates) => {
                          updateHighlight(highlight.id, updates);
                        }} 
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {highlight.items.map((item) => (
                  <MediaItemCard
                    key={item.id}
                    item={item}
                    isHighlightCover={highlight.cover_image_url === item.media_url}
                    onDelete={() => deleteItem(item)}
                    onSetCover={() => setCoverImage(highlight.id, item.media_url)}
                    onUpdateCaption={(caption) => updateItemCaption(item.id, caption)}
                  />
                ))}
                
                {/* Upload button */}
                <label className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors">
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*,video/*"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadMedia(file, highlight.id);
                    }}
                  />
                  {uploading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">Upload</span>
                    </>
                  )}
                </label>
              </div>
              
              {highlight.items.length === 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  No media yet. Upload photos or videos to show in this highlight.
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MediaItemCard({ 
  item, 
  isHighlightCover,
  onDelete, 
  onSetCover,
  onUpdateCaption 
}: { 
  item: HighlightItem;
  isHighlightCover: boolean;
  onDelete: () => void;
  onSetCover: () => void;
  onUpdateCaption: (caption: string) => void;
}) {
  const [showEdit, setShowEdit] = useState(false);
  const [caption, setCaption] = useState(item.caption || "");

  return (
    <div className="relative group">
      <div className={cn(
        "w-24 h-24 rounded-lg overflow-hidden bg-muted",
        isHighlightCover && "ring-2 ring-primary ring-offset-2"
      )}>
        {item.type === "video" ? (
          <div className="relative w-full h-full">
            <video
              src={item.media_url}
              className="w-full h-full object-cover"
              muted
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Video className="w-6 h-6 text-white" />
            </div>
          </div>
        ) : (
          <img
            src={item.media_url}
            alt={item.caption || ""}
            className="w-full h-full object-cover"
          />
        )}
      </div>
      
      {/* Overlay controls */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center gap-1">
        <button
          onClick={onDelete}
          className="p-1.5 rounded-full bg-destructive/80 text-white hover:bg-destructive"
        >
          <Trash2 className="w-3 h-3" />
        </button>
        {item.type === "image" && !isHighlightCover && (
          <button
            onClick={onSetCover}
            className="text-[10px] text-white underline"
          >
            Set as cover
          </button>
        )}
        <button
          onClick={() => setShowEdit(true)}
          className="text-[10px] text-white underline"
        >
          Edit caption
        </button>
      </div>

      {/* Caption edit dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Caption</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="w-full aspect-video rounded-lg overflow-hidden bg-muted">
              {item.type === "video" ? (
                <video src={item.media_url} className="w-full h-full object-contain" controls />
              ) : (
                <img src={item.media_url} alt="" className="w-full h-full object-contain" />
              )}
            </div>
            <div>
              <Label>Caption</Label>
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
              />
            </div>
            <Button 
              onClick={() => {
                onUpdateCaption(caption);
                setShowEdit(false);
              }}
              className="w-full"
            >
              Save Caption
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditHighlightForm({ 
  highlight, 
  onSave 
}: { 
  highlight: Highlight;
  onSave: (updates: Partial<Highlight>) => void;
}) {
  const [title, setTitle] = useState(highlight.title);
  const [description, setDescription] = useState(highlight.description || "");

  return (
    <div className="space-y-4">
      <div>
        <Label>Title</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>
      <Button 
        onClick={() => onSave({ title, description })}
        className="w-full"
      >
        Save Changes
      </Button>
    </div>
  );
}
